import { useCallback, useEffect, useRef, useState } from 'react';
import { usePageAutoRefresh } from '../../hooks/usePageAutoRefresh';
import { INTERNAL_TASKS_CHANGED_EVENT, internalTaskApi } from '../../services/internalTaskApi';

const REFRESH_INTERVAL_MS = 60_000;

// Sidebar „Zadania” badge: the caller's assigned tasks that are not done or
// cancelled (backend GET /internal-tasks/tasks/my-open-count). Loads on mount
// and navigation (refreshKey), right after the caller's own task mutation, and
// via usePageAutoRefresh on focus and once a minute in a visible tab, so other
// people's changes show within a minute. Only the latest response is applied;
// a failed fetch keeps the last known count instead of showing a false zero.
export function useMyOpenTaskCount(enabled: boolean, refreshKey?: string): number {
  const [count, setCount] = useState(0);
  const requestSeq = useRef(0);

  const load = useCallback(async () => {
    const seq = ++requestSeq.current;
    try {
      const value = await internalTaskApi.getMyOpenCount();
      if (seq === requestSeq.current) setCount(value);
    } catch {
      // keep the last known count
    }
  }, []);

  useEffect(() => {
    if (!enabled) {
      requestSeq.current += 1;
      setCount(0);
      return undefined;
    }

    void load();
    const onTasksChanged = () => { void load(); };
    window.addEventListener(INTERNAL_TASKS_CHANGED_EVENT, onTasksChanged);
    return () => {
      requestSeq.current += 1;
      window.removeEventListener(INTERNAL_TASKS_CHANGED_EVENT, onTasksChanged);
    };
  }, [enabled, refreshKey, load]);

  usePageAutoRefresh(load, { enabled, intervalMs: REFRESH_INTERVAL_MS });

  return count;
}
